import { Store } from '@toeverything/infra';

import type { WorkspaceDBService } from '../../db';

export class FolderStore extends Store {
  constructor(private readonly dbService: WorkspaceDBService) {
    super();
  }

  watchNodeInfo(nodeId: string) {
    return this.dbService.db.folders.get$(nodeId);
  }

  watchNodeChildren(parentId: string | null) {
    return this.dbService.db.folders.find$({
      parentId: parentId,
    });
  }

  // watch nodes by type and data (e.g. find all links that point to a given docId)
  watchNodesByData(type: 'doc' | 'tag' | 'collection', data: string) {
    return this.dbService.db.folders.find$({
      type,
      data,
    });
  }

  // find first node synchronously by doc id (link entry whose data === docId)
  findNodeByDocId(docId: string) {
    const matches = this.dbService.db.folders.find({
      type: 'doc',
      data: docId,
    });
    return matches.length > 0 ? matches[0] : null;
  }

  watchIsLoading() {
    return this.dbService.db.folders.isLoading$;
  }

  isAncestor(childId: string, ancestorId: string): boolean {
    if (childId === ancestorId) {
      return false;
    }
    const history = new Set<string>([childId]);
    let current: string = childId;
    while (current) {
      const info = this.dbService.db.folders.get(current);
      if (info === null || !info.parentId) {
        return false;
      }
      current = info.parentId;
      if (history.has(current)) {
        return false; // loop detected
      }
      history.add(current);
      if (current === ancestorId) {
        return true;
      }
    }
    return false;
  }

  createLink(
    parentId: string | null,
    type: 'doc' | 'tag' | 'collection',
    nodeId: string,
    index: string
  ) {
    //  yuanshi-允许创建到根节点
    // const parent = this.dbService.db.folders.get(parentId);
    // if (parent === null || parent.type !== 'folder') {
    //   throw new Error('Parent folder not found');
    // }

    // 检查是否已存在该文档的链接
    const existingLinks = this.dbService.db.folders.find({
      type: type,
      data: nodeId,
    });

    if (existingLinks.length > 0) {
      // 如果已存在，移动到新位置
      const existingLink = existingLinks[0];
      this.moveNode(existingLink.id, parentId, index);
      return existingLink.id;
    } else {
      // 如果不存在，创建新链接
      return this.dbService.db.folders.create({
        parentId,
        type,
        data: nodeId,
        index: index,
      }).id;
    }
  }

  renameNode(nodeId: string, name: string) {
    const node = this.dbService.db.folders.get(nodeId);
    if (node === null) {
      throw new Error('Node not found');
    }
    if (node.type !== 'folder') {
      throw new Error('Cannot rename non-folder node');
    }
    this.dbService.db.folders.update(nodeId, {
      data: name,
    });
  }

  createFolder(parentId: string | null, name: string, index: string) {
    if (parentId) {
      const parent = this.dbService.db.folders.get(parentId);
      if (parent === null || parent.type !== 'folder') {
        throw new Error('Parent folder not found');
      }
    }

    return this.dbService.db.folders.create({
      parentId: parentId,
      type: 'folder',
      data: name,
      index: index,
    }).id;
  }

  removeFolder(folderId: string) {
    const info = this.dbService.db.folders.get(folderId);
    if (info === null || info.type !== 'folder') {
      throw new Error('Folder not found');
    }
    const stack = [info];
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) {
        continue;
      }
      if (current.type !== 'folder') {
        this.dbService.db.folders.delete(current.id);
      } else {
        const children = this.dbService.db.folders.find({
          parentId: current.id,
        });
        stack.push(...children);
        this.dbService.db.folders.delete(current.id);
      }
    }
  }

  removeLink(linkId: string) {
    const link = this.dbService.db.folders.get(linkId);
    if (link === null || link.type === 'folder') {
      throw new Error('Link not found');
    }
    const stack = [link];
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) {
        continue;
      }
      if (current.type !== 'folder') {
        this.dbService.db.folders.delete(current.id);
      } else {
        const children = this.dbService.db.folders.find({
          parentId: current.id,
        });
        stack.push(...children);
        this.dbService.db.folders.delete(current.id);
      }
    }
  }

  moveNode(nodeId: string, parentId: string | null, index: string) {
    const node = this.dbService.db.folders.get(nodeId);
    if (node === null) {
      throw new Error('Node not found');
    }

    if (parentId) {
      if (nodeId === parentId) {
        throw new Error('Cannot move a node to itself');
      }
      if (this.isAncestor(parentId, nodeId)) {
        throw new Error('Cannot move a node to its descendant');
      }
      const parent = this.dbService.db.folders.get(parentId);
      if (
        parent === null
        // yuanshi-20250730: 允许移动到非flor节点
        //  || parent.type !== 'folder'
      ) {
        throw new Error('Parent folder not found');
      }
    } else {
      // if (node.type !== 'folder') {
      //   throw new Error('Root node can only have folders');
      // }
    }
    this.dbService.db.folders.update(nodeId, {
      parentId,
      index,
    });
  }
}
